import { useEffect } from "react"
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useMatches,
  type MetaFunction,
} from "react-router"

import type { Route } from "./+types/root"
import { useRouteLoaderData } from "react-router"
import { PublicNav } from "~/components/layout/public-nav"
import { PublicFooter } from "~/components/layout/public-footer"
import { DocsNav, DocsFooter } from "~/components/layout/docs-chrome"
import { Toaster } from "~/components/ui/sonner"
import { localBusinessSchema } from "~/lib/seo"
import { SITE } from "~/data/site"
import { apiFetch } from "~/lib/api-client"
import { resolveBranding, isNlcOwnHost, type TenantBranding } from "~/lib/branding"
import { BROOMSTACK_BRAND, isHelpHost } from "~/lib/broomstack-brand"
import {
  themeStyleBlock,
  marketingThemeStyleBlock,
  fontStylesheetHref,
  consoleRampFor,
  consoleThemeStyleBlock,
} from "~/lib/theme"
import "./app.css"
import "./marketing.css"

/**
 * Whether New Leaf's own marketing tags may load on this request.
 *
 * These are FIRST-PARTY tags belonging to one company: a Google Ads conversion ID,
 * a Plausible domain, Microsoft Clarity session recording, and the Sprout chatbot.
 * They were loading unconditionally in production, which meant every other tenant's
 * traffic fired conversions into New Leaf's Ads account, every tenant's app showed
 * New Leaf's chatbot, and — the serious one — Clarity SESSION-RECORDED other
 * companies' admins working with their own clients' data, into New Leaf's workspace.
 *
 * That is a cross-tenant leak of behaviour rather than of rows. Tags belong in
 * per-tenant configuration; until that exists they load only on New Leaf's own
 * hostnames, and never on a private surface.
 */
/** Admin, team and client areas are private. Never record or track them. */
const PRIVATE_PREFIXES = [
  "/admin", "/team", "/client", "/super-admin", "/invite",
  "/sign", "/survey", "/tip", "/offer", "/reference",
]

/**
 * Who is this site, actually?
 *
 * The marketing pages render from a static `SITE` constant that names New Leaf
 * Cleaning and hello@nlcstpete.com, baked in at build time and shared by every
 * tenant on this Worker. Resolving the real tenant here, in the ROOT loader,
 * means every public page and the shared footer can read it without each one
 * fetching separately — and it is server-rendered, so a visitor never sees the
 * wrong company's name flash first.
 *
 * The call is free: apiFetch dispatches in-process, and /api/branding reads the
 * tenant row the middleware has already loaded for this request.
 *
 * `branding` is null when the lookup fails. Callers fall back to SITE, which is
 * correct for New Leaf and no worse than today for anyone else.
 */
export async function loader({ request }: Route.LoaderArgs) {
  const url = new URL(request.url)
  const isNlcHost = isNlcOwnHost(url.hostname)
  const isPrivate = PRIVATE_PREFIXES.some((p) => url.pathname.startsWith(p))

  /*
   * The public documentation host names ITSELF.
   *
   * help.broomstack.app carries no tenant (parseSubdomain refuses `help` as a
   * slug), so the branding lookup below would come back empty and every caller
   * would fall through to SITE — which is New Leaf Cleaning, hard-coded. That
   * would put Broomstack's own product manual out under a customer's name,
   * logo and colours. Short-circuit before the lookup rather than after, so
   * there is no window in which the wrong company is the answer.
   */
  if (isHelpHost(url.hostname)) {
    return {
      allowMarketingTags: false,
      branding: BROOMSTACK_BRAND,
      isNlcHost: false,
      origin: url.origin,
      // Read by App() to swap the marketing chrome for the docs site's own.
      // A flag rather than re-deriving the host in the component: the loader
      // already knows, and two places deciding the same thing is how they end
      // up disagreeing.
      isDocsHost: true,
    }
  }

  let branding: TenantBranding | null = null
  try {
    const res = await apiFetch(request, "/api/branding")
    if (res.ok) {
      const body = (await res.json()) as { ok: boolean; data?: TenantBranding }
      if (body.ok && body.data) branding = body.data
    }
  } catch {
    // Never block the page on branding. SITE is the fallback.
  }

  /*
   * `isNlcHost` is passed through as the fallback signal. If the branding
   * lookup fails on someone else's domain we must NOT quietly fall back to
   * New Leaf's name and phone number — that is the exact bug this replaces,
   * and it is the case where a TCPA consent line names the wrong company.
   * Unknown-on-a-foreign-host degrades to neutral copy instead.
   */
  return { allowMarketingTags: isNlcHost && !isPrivate, branding, isNlcHost, origin: url.origin, isDocsHost: false }
}

export const meta: MetaFunction = () => {
  return [
    { charset: "utf-8" },
    { name: "viewport", content: "width=device-width, initial-scale=1" },
    { name: "robots", content: "index, follow" },
    { name: "apple-mobile-web-app-capable", content: "yes" },
    { name: "apple-mobile-web-app-status-bar-style", content: "default" },
    { name: "mobile-web-app-capable", content: "yes" },
  ]
}

/**
 * Global JSON-LD: LocalBusiness + CleaningService on every public page.
 * Admin/team/client portals suppress this via hideChrome.
 *
 * This is New Leaf's own street address, review count, and social links —
 * not a shape any other tenant has data for yet. Stamping another company's
 * NAME onto NLC's address/reviews/socials would be worse than the identity
 * bug this schema had before: a Frankenstein record, not just a misnamed
 * one. So it renders only for New Leaf; other tenants get no LocalBusiness
 * JSON-LD until a real per-tenant data model exists, rather than a
 * synthesized one. Computed once — no per-request I/O, no address to leak.
 */
const GLOBAL_BUSINESS_SCHEMA = localBusinessSchema()

/*
 * NO FAVICON HERE. Deliberately — see the <head> in Layout below.
 *
 * `links` is static: it is computed once at build time with no request context,
 * so anything declared here is the SAME for every tenant. The favicon used to be
 * declared here, pointing at New Leaf's leaf — which meant every company on the
 * platform, and the operator console, served New Leaf's icon in the browser tab.
 * A customer's staff opening their own admin saw another cleaning company's mark.
 *
 * The icon depends on WHO the request is for, so it is emitted in Layout, which
 * can read the resolved tenant from the root loader.
 */
export const links: Route.LinksFunction = () => [
  { rel: "manifest", href: "/manifest.webmanifest" },
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    // Two families only: Fraunces (optical-size serif, H1/H2 display) + Figtree (everything else).
    href: "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400..600;1,9..144,400..600&family=Figtree:ital,wght@0,400..700;1,400..700&display=swap",
  },
]

export function Layout({ children }: { children: React.ReactNode }) {
  /*
   * Read from the root loader rather than a prop: Layout also renders for error
   * boundaries, where no loader data exists. Absent data means "not allowed",
   * which is the safe direction — a tag that fails to load costs an analytics
   * event, a tag that loads on the wrong company's admin records their clients.
   */
  const rootData = useRouteLoaderData<typeof loader>("root")
  const allowTags = rootData?.allowMarketingTags === true
  const branding = resolveBranding(rootData?.branding, rootData?.isNlcHost ?? true)

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/*
          The browser-chrome colour, per tenant.

          Hardcoded to #4A7C59 — New Leaf's green — so every company's staff saw
          New Leaf's brand in their own phone's address bar. It comes from the
          resolved theme now, which falls back to the PLATFORM's neutral palette
          and never to another company's.
        */}
        <meta name="theme-color" content={branding.theme.primary} />
        {/*
          Per-tenant brand colours, server-rendered so there is no flash of the
          wrong company's palette before hydration. Overrides the same custom
          properties app.css declares, so every existing rule picks them up with
          no component changes. Values are hex-validated in lib/theme.ts, so this
          cannot inject.
        */}
        <style dangerouslySetInnerHTML={{ __html: themeStyleBlock(branding.theme) }} />
        {/*
          The company's identity, DERIVED — the console and crew portal's
          `--id-*` family.

          Separate block from the one above because the two carry different
          promises. `themeStyleBlock` emits the tenant's RAW colours, which are
          correct for the browser chrome and the marketing surface, where the
          exact hex is the point. This one emits values computed to hold a WCAG
          ratio against admin.css's frozen ink and surfaces, which is what a
          tool people use all day needs and what the raw hex cannot promise.

          Emitted for every tenant from the first commit, before anything reads
          it, so the tokens can be inspected per tenant in devtools while the
          call sites move over one surface at a time.
        */}
        <style dangerouslySetInnerHTML={{ __html: consoleThemeStyleBlock(consoleRampFor(branding.theme)) }} />
        {/*
          The company's chosen typeface.

          `links` above loads Fraunces + Figtree for every request, because those
          are the platform's own. A tenant that picked a different pairing needs
          its faces too, and the choice is only known per request — so the second
          stylesheet is emitted here rather than declared statically.

          The URL is built from an allowlisted fragment in FONT_CHOICES and
          nothing a customer typed, so it cannot be steered anywhere else. The
          'System' choice returns null and loads nothing at all.
        */}
        {fontStylesheetHref(branding.theme.font) && (
          <link rel="stylesheet" href={fontStylesheetHref(branding.theme.font)!} />
        )}
        {/*
          The marketing surface's OWN palette, for everyone who is not New Leaf.

          `marketing.css` carries a second, independent colour system ("Forest &
          Brass") that the block above does not touch, so every tenant's public
          pages were still painted in New Leaf's green after per-tenant theming
          shipped. Emitted only when this is not New Leaf, so New Leaf's own site
          keeps the CSS defaults exactly and cannot regress from this line.
        */}
        {!branding.isNewLeaf && (
          <style dangerouslySetInnerHTML={{ __html: marketingThemeStyleBlock(branding.theme) }} />
        )}
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content={branding.name} />
        <meta name="mobile-web-app-capable" content="yes" />
        <link rel="manifest" href="/manifest.webmanifest" />
        {/*
          Per-tenant favicon, resolved from the request.

          Order: the company's own uploaded logo → New Leaf's leaf only when the
          request actually resolves to New Leaf → the neutral platform mark.

          That last fallback is the point. Showing our own first customer's icon
          to an unknown host is the same class of mistake as showing them New
          Leaf's phone number, which is what `branding.ts` exists to prevent.
        */}
        <link rel="icon" href={branding.iconHref} />
        <link rel="apple-touch-icon" href={branding.appleIconHref} sizes="180x180" />
        <Meta />
        <Links />
        {import.meta.env.PROD && allowTags && (
          <script
            defer
            data-domain={SITE.domain}
            src="https://plausible.io/js/script.js"
          />
        )}
        {import.meta.env.PROD && allowTags && (
          <>
            <script async src="https://www.googletagmanager.com/gtag/js?id=AW-18172244727" />
            <script dangerouslySetInnerHTML={{ __html: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','AW-18172244727');` }} />
          </>
        )}
      </head>
      <body>
        {children}
        <Toaster position="top-right" richColors />
        <ScrollRestoration />
        <Scripts />
        <script
          dangerouslySetInnerHTML={{
            __html: `if('serviceWorker' in navigator){window.addEventListener('load',function(){navigator.serviceWorker.register('/sw.js')})}`,
          }}
        />
        {/* Sprout — New Leaf's AI assistant. Overlay widget hosted on its own
            Worker; widget.js self-guards to marketing pages (skips /admin,
            /team, /client, and tokenized flows). Gated to New Leaf's own hosts
            as well: its self-guard knows about private PATHS, not about other
            companies, so it was appearing on every tenant's marketing pages. */}
        {allowTags && (
          <script
            defer
            src="https://nlc-sprout-chatbot.tekavraindustries.workers.dev/widget.js"
          />
        )}
      </body>
    </html>
  )
}

export default function App() {
  const matches = useMatches()
  /*
   * Any private surface hides the public chrome.
   *
   * This checked only `isAdmin` and `hideChrome`, but three layouts announce
   * themselves with their own flag — team.tsx `isTeam`, client.tsx `isClient`,
   * super-admin.tsx `isSuperAdmin` — and none of those was in the list. So
   * /team/login, /client/login and /super-admin/login each rendered the full
   * public marketing nav (Quote, Pricing, Service areas) above their own login
   * form, with the public footer beneath it.
   *
   * Every flag is normalised to one boolean here rather than adding three more
   * `||`s at each future call site: a layout that says it is private in ANY of
   * these ways gets no public chrome, and a fourth portal added later only has
   * to name itself. The canonical flag going forward is `hideChrome`; the four
   * role flags are honoured because they already exist and mean the same thing.
   */
  const hideChrome = matches.some((m) => {
    const h = m.handle as
      | {
          isAdmin?: boolean
          isTeam?: boolean
          isClient?: boolean
          isSuperAdmin?: boolean
          hideChrome?: boolean
        }
      | undefined
    if (!h) return false
    return Boolean(
      h.hideChrome || h.isAdmin || h.isTeam || h.isClient || h.isSuperAdmin,
    )
  })

  const rootData = useRouteLoaderData<typeof loader>("root")
  const allowTags = rootData?.allowMarketingTags === true
  const isNewLeaf = resolveBranding(rootData?.branding, rootData?.isNlcHost ?? true).isNewLeaf

  /*
   * Session RECORDING, into New Leaf's Clarity workspace.
   *
   * The most serious of the four tags: unconditional, it recorded other companies'
   * admins moving through their own clients' names, addresses and invoices. Gated
   * to New Leaf's own public pages — never another tenant, never a private surface.
   */
  useEffect(() => {
    if (import.meta.env.PROD && allowTags) {
      import("@microsoft/clarity").then(({ default: Clarity }) => {
        Clarity.init("ws0uhf5tj5")
      })
    }
  }, [allowTags])

  /*
   * The documentation host never renders the marketing chrome.
   *
   * PublicNav is a cleaning company's: "Get a quote", three tenant portal
   * logins, and an on-mount fetch of /api/public/active-crew that 404s here
   * because this host deliberately has no tenant. See docs-chrome.tsx.
   */
  const isDocsHost = Boolean(
    (rootData as { isDocsHost?: boolean } | undefined)?.isDocsHost
  )

  if (isDocsHost) {
    return (
      <>
        <a href="#main-content" className="skip-nav">Skip to main content</a>
        <DocsNav />
        <Outlet />
        <DocsFooter />
      </>
    )
  }

  return (
    <>
      {!hideChrome && (
        <>
          <a href="#main-content" className="skip-nav">Skip to main content</a>
          <PublicNav />
          {isNewLeaf && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(GLOBAL_BUSINESS_SCHEMA),
              }}
            />
          )}
        </>
      )}
      <Outlet />
      {!hideChrome && <PublicFooter />}
    </>
  )
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!"
  let details = "An unexpected error occurred."
  let stack: string | undefined

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error"
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message
    stack = error.stack
  }

  return (
    <section className="error-section" id="main-content">
      <div className="container text-center">
        <h1 className="error-heading">{message}</h1>
        <p className="section-intro">{details}</p>
        {stack && <pre className="error-stack">{stack}</pre>}
        <a href="/" className="btn btn-primary mt-32">Back to Home</a>
      </div>
    </section>
  )
}
